import { useCallback, useState } from 'react';

import { CandidateCheckScreens } from 'constants/candidate_screens';
import { getConsentsData } from 'helpers/candidate_application/consent-data-helpers';
import {
  buildAllFormValues,
  buildNavPath as buildNavPathHelper,
} from 'helpers/candidate_application/form-page-helpers';
import { useQueryParams } from 'hooks/query-params';
import useFormSubmission from 'hooks/use-form-submission';
import usePageVisibilityRedirect from 'hooks/use-page-visibility-redirect';
import useVisibleSubsections from 'hooks/use-visible-subsections';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Loading } from 'components/Loading/Loading';

import {
  getCandidateApplication,
  selectCandidateApplication,
  submitCandidateApplication,
  updateCandidateCheck,
} from 'api/candidate-application-slice';

import FormPageRenderer from '../../FormPages/FormPageRenderer';

const Consents = ({
  candidate,
  brand,
  handlers,
  token,
  sectionKey,
  pageIndex,
  section,
  subsections,
  hasIndexPage,
  isSinglePage,
  fetched,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [admin] = useQueryParams(['admin']);
  const { t: translate } = useTranslation();
  const {
    application: { candidate_checks, check_values },
    candidateFields,
  } = useSelector(selectCandidateApplication);

  const [customError, setCustomError] = useState('');
  const [loading, setLoading] = useState(false);
  const { checkTypeIdRequirements } = candidateFields || {};

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);

  const visibleSubsections = useVisibleSubsections(subsections);
  const { saveFormValuesAndSubmit } = useFormSubmission();

  // Calculate total pages using filtered subsections
  const totalPages = (visibleSubsections || [])?.length || 0;
  const currentPage = parseInt(pageIndex) || 0;

  // Redirect if current page is out of bounds after visibility changes
  usePageVisibilityRedirect({
    currentPage,
    totalPages,
    visibleSubsections,
    preSubsectionFlows: [],
    hasIndexPage: false,
    sectionKey,
    token,
  });

  // Custom submit handler that validates signatures
  const handleFormSubmit = useCallback(
    async (formData, context) => {
      try {
        setCustomError('');
        const subsectionsData = context?.subsections;
        // Find all id_requirements fields across all visible subsections
        const allIdRequirementsFields = (subsectionsData || [])
          .flatMap(sub => sub?.fields || [])
          .filter(field => field?.type === 'id_requirements');
        // Check if any id_requirements field is not met
        const unmetRequirements = (allIdRequirementsFields || [])?.filter(field => {
          if (!field?.code || !field?.hasIdRequirements) return false;
          const requirementsMet = candidateFields?.idRequirementsMet?.[field.code] ?? true;
          return !requirementsMet;
        });

        if (unmetRequirements?.length > 0) {
          // Check if the "not_fulfilled" checkbox is checked for any unmet requirement
          const hasNotFulfilled = (unmetRequirements || [])?.some(field => {
            const fulfillmentKey = `${field.code}_not_fulfilled`;
            const fullFillmentData = formData?.hasOwnProperty(fulfillmentKey)
              ? formData[fulfillmentKey]
              : context?.initialValues?.[fulfillmentKey];
            return !fullFillmentData; // isFullFilled
          });

          if (hasNotFulfilled) {
            setCustomError(translate('candidate.sections.identityRequirementsNotMet'));
            return false;
          }
        }

        setLoading(true);

        const currentPagePrefixes = context?.currentPagePrefixes || null;
        const currentCandidateChecksCount = candidate_checks?.length;
        const result = await dispatch(getCandidateApplication(token)).unwrap();
        const newCandidateChecksCount = result?.candidate_checks?.length;
        if (newCandidateChecksCount > currentCandidateChecksCount) {
          navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
        } else {
          try {
            const systemValues = {
              ...(check_values?.system_values || {}),
              consents_status: 'completed',
            };

            // Collect consents data - for single page, collect all subsections; for multi-page, only current
            // This ensures we don't overwrite signatures from other pages with undefined values
            const currentSubsection = isSinglePage ? null : visibleSubsections[currentPage];
            const consentsData = getConsentsData(
              formData,
              sectionKey,
              currentSubsection ? [currentSubsection] : visibleSubsections,
              {
                allValues: buildAllFormValues({
                  candidateFields,
                  watchValues: formData,
                  currentPagePrefixes,
                }),
                fieldPathIndex: candidateFields?.fieldPathIndex ?? {},
              },
            );

            // Save the data and update state - let the hook handle comprehensive value collection
            await saveFormValuesAndSubmit(
              sectionKey,
              formData,
              token,
              systemValues,
              currentPagePrefixes,
              false, // rejectedValuesFix
              consentsData,
            );

            // Update id_requirements_met for each candidate check based on checkTypeIdRequirements
            if (checkTypeIdRequirements && candidate_checks && Array.isArray(candidate_checks)) {
              const updatePromises = candidate_checks.map(async check => {
                const checkTypeSlug = check?.type;
                if (!checkTypeSlug || !check?.id) return;

                const checkTypeData = checkTypeIdRequirements[checkTypeSlug];
                if (checkTypeData && typeof checkTypeData.requirementsMet === 'boolean') {
                  try {
                    await dispatch(
                      updateCandidateCheck({
                        token,
                        id: check.id,
                        data: { id_requirements_met: checkTypeData.requirementsMet },
                      }),
                    ).unwrap();
                  } catch (error) {
                    // Log error but don't block submission
                    console.error(
                      `Failed to update id_requirements_met for check ${check.id}:`,
                      error,
                    );
                  }
                }
              });

              // Wait for all updates to complete
              await Promise.all(updatePromises);
            }

            await dispatch(
              submitCandidateApplication({ token, user_agent: navigator.userAgent }),
            ).unwrap();

            const successUrl = `/form_submission/candidate/${CandidateCheckScreens.SUCCESS}/${token}`;
            navigate(buildNavPath(successUrl));
            setLoading(false);
            return false; // Prevent FormPageRenderer from running its own navigation
          } catch (submitError) {
            setLoading(false);
            throw submitError; // Re-throw to be caught by outer catch
          }
        }
      } catch (error) {
        console.error('Error submitting consents:', error);
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
        setLoading(false);
      }
    },
    [
      dispatch,
      navigate,
      token,
      candidate,
      saveFormValuesAndSubmit,
      sectionKey,
      candidateFields,
      candidate_checks,
      check_values,
      checkTypeIdRequirements,
      visibleSubsections,
      currentPage,
      isSinglePage,
      translate,
      buildNavPath,
    ],
  );

  return (
    <>
      <Loading active={loading} />
      <FormPageRenderer
        subsections={visibleSubsections}
        candidate={candidate}
        brand={brand}
        handlers={handlers}
        fetched={fetched}
        singlePage={isSinglePage}
        sectionKey={sectionKey}
        pageIndex={isSinglePage ? undefined : currentPage}
        globalPageIndex={currentPage}
        totalPages={totalPages}
        primaryActionLabel={translate('common.submitApplication')}
        customError={customError}
        onFormSubmit={handleFormSubmit}
      />
    </>
  );
};

export default Consents;
